<template>
  <div>
    <div
      :class="isSuperCustomer ? 'bg-primary brx-8 p-3' : 'bg-grey brx-8 p-3'"
    >
      <div class="container">
        <div class="row align-items-start">
          <!-- COL 1 -->
          <div
            :class="
              isContainerSmall 
                ? 'col-12'
                : 'col-12 col-md-6 col-lg-5 mb-3'
            "
          >
            <div class="d-flex flex-column align-items-center text-center">
              <!-- Avatar -->
              <div class="gradient-border position-relative mb-2">
                <img
                  :src="generateFullUrlWithToken(profile.profile_picture)"
                  class="rounded-circle hpx-80 d-block"
                  @error="$event.target.src = '/img/user_default_pic.png'"
                  alt="Profile"
                />
                <div
                  v-if="isPrivate"
                  class="position-absolute"
                  style="top: -5px; left: -5px"
                >
                  <i
                    class="fs-14 fa-solid fa-triangle-exclamation text-primary"
                    v-c-tooltip="{
                      content: 'Private Profile',
                      appendToBody: true,
                      placement: 'right',
                    }"
                  ></i>
                </div>
                <div
                  v-if="inProcessCount"
                  class="position-absolute"
                  style="top: -5px; right: -5px"
                >
                  <span
                    class="text-primary font-weight-bold border-circle border-primary"
                    v-c-tooltip="{
                      content: 'In Process',
                      appendToBody: true,
                      placement: 'left',
                    }"
                    >P</span
                  >
                </div>
              </div>

              <!-- Basic details -->
              <h5 class="mb-1 fw-700 mt-2">
                {{ profile._title | toTitleCase }}.
                {{ profile.first_name | toTitleCase }}
                {{ profile.surname | toTitleCase }}
              </h5>
              <p class="mb-1" v-if="profile.candidateTypeName">
                {{ profile.candidateTypeName }}
              </p>
              <p class="mb-1">
                <span v-if="profile.genderName">{{ profile.genderName }}</span>
                <span class="nowrap" v-if="profile.age"
                  >, {{ profile.age }} Years</span
                >
              </p>
              <p class="mb-1">
                <span class="text-break"> {{ profile.email }} </span>
              </p>
              <p class="mb-1">
                <i class="fa-solid fa-globe mr-1"></i
                >{{ profile.nationalityName }}
              </p>
              <!-- IDs / badges -->
              <div class="mt-2 small">
                <div>
                  <span>Candidate ID: </span
                  ><span class="fw-bold">{{ profile.display_uid }}</span>
                </div>
                <!-- <div v-show="profile.original_ohr_id != null">
                  <span>Old CRM ID: </span
                  ><span class="fw-bold">{{ profile.original_ohr_id }}</span>
                </div> -->
                <div
                  class="mt-1"
                  v-if="!isUKMainAgency && isOrgBadge && !isAgencyBadge"
                >
                  <CBadge color="danger" class="p-1">
                    {{ profile.organisation_name }}
                  </CBadge>
                  <span
                    class="three-dots mx-1"
                    v-c-tooltip="{
                      content: toolTip,
                      appendToBody: true,
                      placement: 'right',
                    }"
                  >
                    <i class="fa-solid fa-ellipsis-vertical"></i>
                  </span>
                </div>
              </div>

              <!-- Meta chips -->
              <div class="d-flex flex-wrap justify-content-center mt-2">
                <span class="meta-chip"
                  ><i class="fa-solid fa-calendar-days mr-1"></i
                  >{{ profile.createdOnDate }}</span
                >
                <span class="meta-chip"
                  ><i class="fa-solid fa-file-import mr-1"></i
                  >{{ profile.lastLoginDate }}</span
                >
              </div>
            </div>
          </div>

          <!-- COL 2 -->
          <div
            :class="
              isContainerSmall 
                ? 'col-12'
                : 'col-12 col-md-6 col-lg-4 mb-3'
            "
          >
            <div class="contact-list">
              <div
                v-for="(field, index) in contactDetails"
                :key="index"
                class="contact-row"
              >
                <span class="contact-icon-circle">
                  <i :class="field.icon" class="text-primary"></i>
                </span>

                <!-- Link with URL-->
                <template v-if="field.isLink">
                  <a
                    v-if="field.value"
                    class="contact-link text-truncate"
                    target="_blank"
                    :href="normalizeUrl(field.value)"
                  >
                    {{ field.label }}
                  </a>
                  <span v-else class="contact-value text-muted">--</span>
                </template>
                <template v-else>
                  <span class="contact-value text-truncate">
                    {{ field.value || "--" }}
                  </span>
                </template>
              </div>
            </div>
          </div>

          <!-- COL 3 -->
          <div
            :class="
              isContainerSmall 
                ? 'col-12'
                : 'col-12 col-md-6 col-lg-3 mb-3'
            "
          >
            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
            >
              <CButton
                v-if="isEditable"
                size="sm"
                class="button"
                :class="isSuperCustomer ? 'bg-blue-light-3' : 'text-primary border-primary'"
                @click="openContactInfoEditModal"
              >
                <i class="fas fa-edit mr-2"></i> Edit
              </CButton>
            </div>

            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
            >
              <CButton
                v-if="!isCandidateRole"
                class="button"
                :class="isSuperCustomer ? 'bg-indigo-light' : 'btn-primary'"
                @click="openCommentModal()"
              >
                Private Comments({{ getComments.length }})
              </CButton>
            </div>

            <div
              v-if="enableAddScratchPad"
              class="d-flex justify-content-start justify-content-lg-end mb-2"
            >
              <CButton
                class="button"
                :class="isSuperCustomer ? 'bg-indigo-light' : 'btn-primary'"
                @click="addToScratch"
              >
                <i class="fa-solid fa-circle-plus mr-2"></i>
                Add to Scratch Pad
              </CButton>
            </div>

            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
            >
              <CButton
                v-if="
                  !isCandidateRole &&
                  !isCandidateSupplierFromAccessToken &&
                  !isEducationalInstitute
                "
                :class="[
                  'button',
                  isSuperCustomer ? 'bg-indigo-light' : 'btn-primary',
                ]"
                @click="openFrontSheet"
              >
                <i class="fas fa-download mr-2"></i> Front Sheet
              </CButton>
            </div>

            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
            >
              <CButton
                class="button"
                :class="isSuperCustomer ? 'bg-gradient' : 'btn-primary'"
                :disabled="!latestResume"
                @click="latestResume && preview(latestResume)"
              >
                <i class="fas fa-eye mr-2"></i>
                {{ latestResume?.lable || "CV/Resume" }}
              </CButton>
            </div>

            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
              v-if="latestAgencyCV && isJobSupplier"
            >
              <CButton
                class="button"
                :class="isSuperCustomer ? 'bg-indigo-light' : 'btn-primary'"
                @click="preview(latestAgencyCV)"
              >
                <i class="fas fa-eye mr-2"></i> {{ latestAgencyCV?.lable }} 
              </CButton>
            </div>

            <div
              class="d-flex justify-content-start justify-content-lg-end mb-2"
              v-else-if="isJobSupplier && !latestAgencyCV"
            >
              <CButton
                name="btn_req_agency_cv"
                class="button"
                :class="isSuperCustomer ? 'bg-indigo-light' : 'btn-primary'"
                :disabled="isAgencyCVReqPending"
                @click="openReqAgencyModal"
              >
                Request Agency CV
              </CButton>
            </div>
          </div>
        </div>

        <!-- Admin actions under all columns -->
        <CRow
          v-if="currentUserRole === Role.systemAdmin"
          class="mt-3 justify-content-center"
        >
          <CButton
            v-if="!profile.deleted"
            name="Delete"
            color="primary"
            class="px-2 f-12 col-4"
            @click="candidateState($event)"
            >Delete</CButton
          >
          <CButton
            v-if="profile.deleted"
            name="Restore"
            color="primary"
            class="px-2 f-12 col-4"
            @click="candidateState($event)"
            >Restore</CButton
          >
          <CButton
            v-if="
              (profile.available_during_covid ||
                profile.available_during_covid === null) &&
              !profile.deleted
            "
            name="Deactivate"
            variant="outline"
            color="primary"
            class="ml-2 f-12 px-1 col-4"
            @click="candidateState($event)"
            >Deactivate</CButton
          >
          <CButton
            v-if="profile.available_during_covid === false && !profile.deleted"
            name="Reactivate"
            variant="outline"
            color="primary"
            class="ml-2 px-1 f-12 col-4"
            @click="candidateState($event)"
            >Reactivate</CButton
          >
        </CRow>
      </div>
    </div>

    <!-- Modal components remain unchanged -->
    <ContactInfoEdit
      :candidateInfo="profile"
      :isShowPopup="showContactInfoEditModal"
      @modalCallBack="contactInfoModalCallBack"
    />
    <PopupModal
      v-if="popupModal.isShowPopup"
      :modalTitle="popupModal.modalTitle"
      :modalColor="popupModal.modalColor"
      :modalContent="popupModal.modalContent"
      :modalLabel="popupModal.modalLabel"
      :isShowPopup="popupModal.isShowPopup"
      :popupModalCallBack="popupModalCallBack"
      :modalJob="popupModal.modalJob"
      :modalReasonType="popupModal.modalReasonType"
    />
    <PreviewModal
      v-if="previewModal.isShowPopup"
      :modalTitle="previewModal.modalTitle"
      :modalColor="previewModal.modalColor"
      :isShowPopup="previewModal.isShowPopup"
      :buttons="previewModal.buttons"
      :modalCallBack="modalCallBack"
      :isPreview="previewModal.isPreview"
      :iframe="iframe"
      :size="previewModal.size"
      :closeOnBackdrop="previewModal.closeOnBackdrop"
    />
    <CandidateFrontsheet
      v-if="frontSheetModal.isShowPopup"
      :isShowPopup="frontSheetModal.isShowPopup"
      @modalCallBack="frontSheetModalCallBack"
      :candidateDetail="frontSheetModal.candidate"
      :jobDetail="frontSheetModal.job"
      :frontSheetInfo="frontSheetModal.frontSheetInfo"
      :frontSheets="frontSheetModal.comments"
    />
    <Modal
      v-if="reqAgencyModal.isShowPopup"
      :modalTitle="reqAgencyModal.modalTitle"
      :modalColor="reqAgencyModal.modalColor"
      :modalContent="reqAgencyModal.modalContent"
      :isShowPopup="reqAgencyModal.isShowPopup"
      :buttons="reqAgencyModal.buttons"
      :modalCallBack="reqAgencyModalCallBack"
      :closeOnBackdrop="false"
    />
    <ScratchPadModal
      v-if="scratchpad.isShowPopup"
      :todo="scratchpad.todo"
      :isShowPopup="scratchpad.isShowPopup"
      @modalCallBack="scratchPadModalCallBack"
    />
    <CandidateCommentModal
      v-if="commentModal.isShowPopup"
      :isShowPopup="commentModal.isShowPopup"
      :candidateID="commentModal.candidateID"
      @commentModalCallBack="commentModalCallBack"
    />
  </div>
</template>

<script>
import _ from "lodash";
import { getScope, Role, generateFullUrlWithToken } from "@/helpers/helper";
import { mapActions, mapGetters } from "vuex";
import pdf from "vue-pdf";
import { BASE_URL } from "@/service_urls";
import isJobSupplier from "@/mixins/isJobSupplier";
import PreviewModal from "@/components/reusable/PreviewModal";
import PopupModal from "@/components/reusable/PopupModal";
import Modal from "@/components/reusable/Modal";
import CandidateFrontsheet from "@/containers/Frontsheet/CandidateFrontsheet";
import ScratchPadModal from "@/containers/Dashboard/RecruiterDashBoard/ScratchPadModal";
import ContactInfoEdit from "./EditForm/ContactInfoEdit";
import CandidateCommentModal from "@/containers/CandidateDetailsTalentFind/ProfileSection/CandidateCommentModal";

export default {
  mixins: [isJobSupplier],
  props: {
    profile: {
      type: Object,
      default: {},
    },
    agencyCVDocument: {
      type: Array,
      default: () => [],
    },
    resumeDocument: {
      type: Array,
      default: () => [],
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    enableAddScratchPad: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    Modal,
    PreviewModal,
    PopupModal,
    CandidateFrontsheet,
    ScratchPadModal,
    ContactInfoEdit,
    CandidateCommentModal,
  },
  data() {
    return {
      currentUserRole: getScope(),
      Role,
      iframe: {
        src: "",
        loaded: false,
        isImg: false,
        pdf: {
          ispdf: false,
          src: "",
          numPages: undefined,
          addContentClasses: "",
        },
      },
      previewModal: {
        modalColor: "primary",
        modalTitle: "Document Preview",
        isShowPopup: false,
        buttons: ["OK"],
        isPreview: false,
        size: "lg",
        closeOnBackdrop: false,
      },
      frontSheetModal: {
        isShowPopup: false,
        candidate: null,
        job: null,
        frontSheetInfo: null,
        comments: [],
      },
      reqAgencyModal: {
        isShowPopup: false,
        modalTitle: "Request Agency CV",
        modalContent:
          "Please confirm you would like to request the CV for this candidate?",
        modalColor: "primary",
        buttons: ["No", "Yes"],
      },
      popupModal: {
        modalColor: "",
        modalTitle: "",
        modalContent: "",
        modalLabel: "Select Reason Code",
        isShowPopup: false,
        modalJob: "",
        modalReasonType: "",
      },
      scratchpad: {
        isShowPopup: false,
        toDo: {},
      },
      commentModal: {
        isShowPopup: false,
        candidateID: null,
      },
      generateFullUrlWithToken,
      editingField: null,
      tempFieldValue: "",
      editableFields: ["Email", "Phone", "Whatsapp", "Web", "Location"],
      showContactInfoEditModal: false,
      isContainerSmall: false,
    };
  },
  computed: {
    ...mapGetters([
      "isCandidateSupplierFromAccessToken",
      "getGenderColor",
      "getUserId",
      "getOrgIDFromAccessToken",
      "getAgencyCVRequest",
      "getCustomerId",
      "getUserId",
      "getIsParentUser",
      "isUKMainAgency",
      "isHeadlessClient",
      "getJobCandidateCount",
      "getCandidateCommonComments",
      "isSuperCustomer",
      "isEducationalInstitute",
    ]),
    isPrivate() {
      return this.profile?.is_private || false;
    },
    isAgencyBadge() {
      return (
        (this.profile.agency_contact_id &&
          this.profile.organisation_name &&
          this.isUKMainAgency &&
          this.currentUserRole !== this.Role.systemAdmin) ||
        false
      );
    },
    isOrgBadge() {
      return (
        (this.profile.organisation_id != this.getOrgIDFromAccessToken &&
          this.getIsParentUser) ||
        false
      );
    },
    isAgencyCVReqPending() {
      return (
        this.getAgencyCVRequest.filter(
          ({ candidate_id, date_uploaded, req_org_id }) =>
            candidate_id === this.profile?.candidate_uid &&
            date_uploaded == null &&
            req_org_id === this.getOrgIDFromAccessToken
        ).length > 0 || false
      );
    },
    isCandidateRole() {
      return (
        this.currentUserRole === Role.customerCandidate ||
        this.currentUserRole === Role.systemCandidate
      );
    },
    latestResume() {
      return _.maxBy(this.resumeDocument, "version_no");
    },
    latestAgencyCV() {
      return _.maxBy(this.agencyCVDocument, "version_no");
    },
    inProcessCount() {
      return (
        (this.getJobCandidateCount && this.getJobCandidateCount[4]) || null
      );
    },
    isShowPrcessBadge() {
      return !this.isCandidateRole && !this.isCustomerUserRole;
    },
    isCustomerUserRole() {
      return this.currentUserRole === Role.customerUser;
    },
    toolTip() {
      return this.profile.shared_organisations.map((i) => i.name).join(", ");
    },
    getComments() {
      return this.getCandidateCommonComments;
    },
    contactDetails() {
      return [
        {
          icon: "fa-solid fa-phone",
          label: "Phone",
          value: this.profile.phone,
        },
        {
          icon: "fa-brands fa-whatsapp",
          label: "Whatsapp",
          value: this.profile.whatsappNumber,
        },
        {
          icon: "fa-solid fa-location-dot",
          label: "Location",
          value: this.profile.current_country?.country_name || "---",
        },
        {
          icon: "fa-solid fa-globe",
          label: "Web",
          value: this.profile.personal_website,
          isLink: true,
        },
        {
          icon: "fa-brands fa-linkedin",
          label: "LinkedIn",
          value: this.profile.linkedin_address,
          isLink: true,
        },
        {
          icon: "fa-brands fa-skype",
          label: "Skype",
          value: this.profile.skype_id,
          isLink: true,
        },
      ];
    },
    isCandidateRole() {
      return (
        this.currentUserRole === Role.customerCandidate ||
        this.currentUserRole === Role.systemCandidate
      );
    },
  },
  methods: {
    ...mapActions([
      "createAgencyCVRequest",
      "postScratchPad",
      "deleteCandidate",
      "deactivateCandidate",
      "restoreCandidate",
      "reactivateCandidate",
      "getCandidateDetails",
      "updateCandidate",
      "fetchCandidateCommonComments",
    ]),
    showPreview(data) {
      const { link } = data;
      return generateFullUrlWithToken(link);
    },
    preview(data) {
      data.link_to_document = this.showPreview(data);
      var _baseUrl = BASE_URL;
      var _base_domain = "https://" + window.location.host;
      this.iframe.url = data.link_to_document;
      if (["PNG", "JPG", "JPEG", "GIF"].includes(data?.document_ext)) {
        this.iframe.loaded = false;
        this.iframe.ispdf = false;
        this.iframe.isImg = true;
        this.iframe.src = data.link_to_document;
        this.previewModal.isPreview = true;
        this.previewModal.isShowPopup = true;
      } else if (data?.document_ext === "PDF") {
        this.iframe.isImg = false;
        this.iframe.loaded = false;
        this.iframe.pdf.ispdf = true;
        this.iframe.pdf.src = pdf.createLoadingTask(data.link_to_document);
        setTimeout(() => {
          this.iframe.pdf.src.promise.then((pdf) => {
            this.iframe.pdf.numPages = pdf.numPages;
          });
        }, 100);
        this.previewModal.isPreview = true;
        this.previewModal.isShowPopup = true;
      } else if (
        ["TXT", "EPS", "TIF", "TIFF", "RAW"].includes(data?.document_ext)
      ) {
        this.showToast({
          class: "bg-danger text-white",
          message: "The selected file type is not available for Preview.",
        });
        this.iframe.src = "";
        this.iframe.loaded = false;
        this.iframe.isImg = false;
        this.iframe.pdf.ispdf = false;
        this.previewModal.isPreview = false;
        this.previewModal.isShowPopup = false;
      } else {
        this.iframe.isImg = false;
        this.iframe.ispdf = false;
        this.iframe.loaded = true;
        this.iframe.src =
          "https://view.officeapps.live.com/op/embed.aspx?src=" +
          data.link_to_document;
        this.previewModal.isPreview = true;
        this.previewModal.isShowPopup = true;
      }
    },
    openFrontSheet() {
      this.frontSheetModal.candidate = this.profile;
      this.frontSheetModal.isShowPopup = true;
      this.frontSheetModal.comments = [];
    },
    frontSheetModalCallBack(action, payload) {
      this.frontSheetModal.isShowPopup = false;
    },
    modalCallBack(action) {
      this.previewModal.isPreview = false;
      this.previewModal.isShowPopup = false;
      this.iframe.src = "";
      this.iframe.loaded = false;
      this.iframe.isImg = false;
      this.iframe.pdf.ispdf = false;
    },
    candidateState(event) {
      let name = event.currentTarget.name;
      let cname = {
        delete: "Delete",
        deactivate: "Deactivate",
      };
      this.popupModal.modalReasonType = name.toUpperCase();
      this.popupModal.modalTitle = `${name} Candidate`;
      this.popupModal.modalContent = `Are you sure to ${name.toLowerCase()} the candidate? If Yes please select an appropriate reason..`;
      this.popupModal.isShowPopup = true;
      if (name === cname.delete || name === cname.deactivate) {
        this.popupModal.modalColor = "danger";
      } else {
        this.popupModal.modalColor = "primary";
      }
    },
    popupModalCallBack(action, value) {
      let appendAction;
      const { modalTitle } = this.popupModal;
      const _action = {
        confirm: "Confirm",
        cancel: "Cancel",
        deleteCandidate: "Delete Candidate",
        deactivateCandidate: "Deactivate Candidate",
        restoreCandidate: "Restore Candidate",
        reactivateCandidate: "Reactivate Candidate",
      };
      if (action === _action.confirm) {
        let payload = {
          candidate_id: this.profile.candidate_uid,
          reason_id: value.reason_id,
          comments: value.comments,
          customer_uid: this.profile?.customer_uid,
        };
        //Delete
        if (modalTitle === _action.deleteCandidate) {
          appendAction = this.deleteCandidate(payload);
        }
        //Deactivate
        else if (modalTitle === _action.deactivateCandidate) {
          appendAction = this.deactivateCandidate(payload);
        }
        //Restore
        else if (modalTitle === _action.restoreCandidate) {
          appendAction = this.restoreCandidate(payload);
        }
        //Reactivate
        else if (modalTitle === _action.reactivateCandidate) {
          appendAction = this.reactivateCandidate(payload);
        }
        Promise.all([appendAction]).then((res) => {
          this.getCandidateDetails(this.$route.params.id);
        });
      }
      this.popupModal.isShowPopup = false;
    },
    openReqAgencyModal() {
      this.reqAgencyModal.isShowPopup = true;
    },
    reqAgencyModalCallBack(action) {
      if (action === "Yes") {
        const payload = {
          req_org_id: this.getOrgIDFromAccessToken,
          req_user_id: this.getUserId,
          candidate_id: this.profile?.candidate_uid,
        };
        this.createAgencyCVRequest(payload);
      }
      this.reqAgencyModal["isShowPopup"] = false;
    },
    addToScratch() {
      const name = `<a href='/candidate/${this.profile.candidate_uid
        }'>${this.toTitleCase(this.profile.first_name)} ${this.toTitleCase(
          this.profile.surname
        )} (ID-${this.profile.display_uid})</a>`;
      this.scratchpad.todo = {
        title: `Candidate - ${this.toTitleCase(
          this.profile.first_name
        )} ${this.toTitleCase(this.profile.surname)} (ID-${this.profile.display_uid
          })`,
        description: `<p>Please review candidate ${name}</p>`,
      };
      this.scratchpad.isShowPopup = true;
    },
    scratchPadModalCallBack(action, payload) {
      if (action && payload) {
        this.postScratchPad(payload);
      }
      this.scratchpad.isShowPopup = false;
      this.scratchpad.todo = null;
    },
    toTitleCase(str) {
      if (str) return _.startCase(str);
      return str;
    },
    openContactInfoEditModal() {
      this.showContactInfoEditModal = true;
    },

    contactInfoModalCallBack(action, data) {
      if (action === false) this.showContactInfoEditModal = false;
      if (action === true && data != null) {
        const { customer_uid } = this.profile;
        let appentAction = this.updateCandidate({ data, customer_uid });
        Promise.all([appentAction]).then((res) => {
          if (res) this.showContactInfoEditModal = false;
        });
      }
    },
    openCommentModal() {
      this.commentModal.candidateID = this.profile?.candidate_uid;
      this.commentModal.isShowPopup = true;
    },
    commentModalCallBack() {
      this.commentModal.isShowPopup = false;
    },
    normalizeUrl(url) {
      if (!url) return "#";
      const v = String(url).trim();
      if (/^https?:\/\//i.test(v)) return v;
      if (/^\/\//.test(v)) return `https:${v}`;
      return `https://${v.replace(/^\/+/, "")}`;
    },
    checkContainerSize() {
      const container = this.$el.querySelector('.container');
      if (container) {
        this.isContainerSmall = container.offsetWidth < 600;
    }
  }
  },
  filters: {
    toTitleCase(str) {
      if (str) return _.startCase(str);
      return str;
    },
  },
  mounted() {
    this.fetchCandidateCommonComments(this.profile?.candidate_uid);
    this.checkContainerSize();
    window.addEventListener('resize', this.checkContainerSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkContainerSize);
  },
};
</script>

<style lang="scss" scoped>
.bg-grey {
  background-color: #eeeeee !important;
  color: rgb(20, 20, 20);
}

.fs-14 {
  font-size: 14px !important;
}

.hpx-80 {
  height: 80px;
  width: 80px;
  object-fit: cover;
}

.gradient-border {
  position: relative;
  border-radius: 50%;
  padding: 3px;
  background: linear-gradient(135deg, #3a7bd5, #00d2ff);
}

.brx-8 {
  border-radius: 8px;
}

.br-50 {
  border-radius: 50px;
}

.cursor-pointer-hover-gradient:hover {
  background: linear-gradient(135deg, #3a7bd5, #00d2ff) !important;
}

.min-hpx-30 {
  min-height: 30px;
}

.min-wpx-30 {
  min-width: 30px;
}

.brb-blue-light {
  border-bottom: 1px solid #e0f2ff;
}

.cursor-pointer {
  cursor: pointer;
}

.position-relative {
  position: relative;
}

.edit-contact-btn {
  margin-top: 1rem;
  text-align: right;
}

.meta-chip {
  font-size: 12px;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 6px 4px 6px;
  display: inline-flex;
  align-items: center;
}
.meta-chip i {
  font-size: 12px;
}

.chip-action {
  background: #6c757d;
  color: #fff;
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.chip-action small {
  line-height: 1;
}

.contact-list {
  margin-top: 6px;
}
.contact-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.contact-row:last-child {
  border-bottom: none;
}
.contact-icon {
  min-width: 20px;
  text-align: center;
  font-size: 14px;
  color: #ffffff;
  opacity: 0.9;
}
.contact-value {
  flex: 1;
  min-width: 0;
  // color: white;
  word-break: break-word;
}
.contact-value a,
a.contact-value {
  color: white !important;
  text-decoration: none;
}

@media (max-width: 576px) {
  .chip-action {
    padding: 6px 10px;
  }
  .meta-chip {
    margin-bottom: 6px;
  }
}

.contact-icon-circle {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
// .contact-icon-circle i {
//   font-size: 14px;
//   color: var(--primary, #0d6efd);
// }

.contact-link {
  // color: #ffffff !important;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 600;
}
.contact-link:hover,
.contact-link:focus {
  // color: #ffffff;
  text-decoration-thickness: 2px;
}

.button {
  min-width: 100%;
}
</style>
